import { useState, useRef, useEffect } from "react";
import { MessageCircle, Send, X, Calculator, MessagesSquare } from "lucide-react";
import { readPendingJob, runAnalysisJob, savePendingJob } from "../api/llmJobs";
import { apiPost } from "../api/client";

interface CopilotMessage {
  role: "user" | "assistant";
  content: string;
  toolsUsed?: string[];
}

interface CopilotResponse {
  question: string;
  answer: string;
  tools_used: string[];
  context: any[];
  llm_call_id: string | null;
}

interface SimulationResult {
  notional: number;
  margin: number;
  liquidation_price: number;
  funding_cost: number;
  fees: number;
  total_friction: number;
  roe_breakeven_pct: number;
}

/** Lightweight markdown-to-HTML renderer for copilot responses.
 *  Styling is handled by `.copilot-md` CSS rules — no inline styles. */
function renderMarkdown(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/```([\s\S]*?)```/g, (_m, code) => `<pre>${code}</pre>`)
    .replace(/`([^`]+)`/g, "<code>$1</code>")
    .replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>")
    .replace(/^### (.+)$/gm, "<h3>$1</h3>")
    .replace(/^## (.+)$/gm, "<h2>$1</h2>")
    .replace(/^# (.+)$/gm, "<h1>$1</h1>")
    .replace(/^[-*] (.+)$/gm, "<ul><li>$1</li></ul>")
    .replace(/<\/ul>\s*<ul>/g, "")
    .replace(/\n/g, "<br />");
}

interface CopilotWidgetProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  showFab?: boolean;
}

export default function CopilotWidget({
  open: controlledOpen,
  onOpenChange,
  showFab = true,
}: CopilotWidgetProps = {}) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = controlledOpen !== undefined ? controlledOpen : internalOpen;

  const setOpen = (val: boolean) => {
    setInternalOpen(val);
    onOpenChange?.(val);
  };

  const [activeTab, setActiveTab] = useState<"chat" | "sim">("chat");
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingSecs, setLoadingSecs] = useState(0);
  const [messages, setMessages] = useState<CopilotMessage[]>([
    {
      role: "assistant",
      content:
        "안녕하세요! KORU 관제탑 코파일럿입니다. 현재 포지션, 리스크 지표, 최근 뉴스 등에 대해 질문하거나, 시뮬레이션 탭에서 포지션 시나리오를 계산할 수 있습니다.",
    },
  ]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Simulation state
  const [simEntry, setSimEntry] = useState("100");
  const [simQty, setSimQty] = useState("1");
  const [simLev, setSimLev] = useState("10");
  const [simFunding, setSimFunding] = useState("0.0001");
  const [simHours, setSimHours] = useState("24");
  const [simResult, setSimResult] = useState<SimulationResult | null>(null);
  const [simLoading, setSimLoading] = useState(false);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const pendingJob = useRef<Record<string, unknown> | null>(readPendingJob('copilot'));
  const jobController = useRef<AbortController | null>(null);
  useEffect(() => {
    const pending = readPendingJob('copilot');
    if (!pending?.request_id) return;
    const controller = new AbortController(); jobController.current = controller;
    setLoading(true); setMessages([{ role: 'user', content: String(pending.question || '') }]);
    runAnalysisJob<CopilotResponse>('copilot', pending, { resume: true, signal: controller.signal })
      .then(result => { if (!controller.signal.aborted) { pendingJob.current = null; setMessages(prev => [...prev, { role: 'assistant', content: result.answer, toolsUsed: result.tools_used }]); } })
      .catch(err => { if (!controller.signal.aborted) setMessages(prev => [...prev, { role: 'assistant', content: String(err instanceof Error ? err.message : err) }]); })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => controller.abort();
  }, []);
  useEffect(() => () => jobController.current?.abort(), []);

  // Focus the input when the chat tab opens.
  useEffect(() => {
    if (isOpen && activeTab === "chat") {
      inputRef.current?.focus();
    }
  }, [isOpen, activeTab]);

  // Track elapsed seconds while awaiting deep copilot analysis
  useEffect(() => {
    let timer: any;
    if (loading) {
      setLoadingSecs(0);
      timer = setInterval(() => {
        setLoadingSecs((s) => s + 1);
      }, 1000);
    } else {
      setLoadingSecs(0);
    }
    return () => clearInterval(timer);
  }, [loading]);

  async function handleSend() {
    const question = input.trim();
    if (!question || loading) return;

    setInput("");
    setLoading(true);
    setMessages((prev) => [...prev, { role: "user", content: question }]);

    try {
      const payload = pendingJob.current?.question === question ? pendingJob.current : { request_id: crypto.randomUUID(), question };
      pendingJob.current = payload; savePendingJob('copilot', payload);
      jobController.current?.abort(); jobController.current = new AbortController();
      const result = await runAnalysisJob<CopilotResponse>('copilot', payload, { signal: jobController.current.signal });
      pendingJob.current = null;
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: result.answer,
          toolsUsed: result.tools_used,
        },
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: "assistant",
          content: `오류가 발생했습니다: ${err instanceof Error ? err.message : "알 수 없는 오류"}`,
        },
      ]);
    } finally {
      setLoading(false);
    }
  }

  async function handleSimulate() {
    setSimLoading(true);
    try {
      const result = await apiPost<{ simulation: SimulationResult }>(
        "/llm/copilot/simulate",
        {
          entry_price: parseFloat(simEntry),
          qty: parseFloat(simQty),
          leverage: parseFloat(simLev),
          funding_rate_hourly: parseFloat(simFunding),
          hours: parseInt(simHours),
        },
      );
      setSimResult(result.simulation);
    } catch {
      setSimResult(null);
    } finally {
      setSimLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  }

  if (!isOpen) {
    if (!showFab) return null;
    return (
      <button
        onClick={() => setOpen(true)}
        className="copilot-fab"
        aria-label="코파일럿 열기"
      >
        <MessageCircle size={20} />
        <span>코파일럿</span>
      </button>
    );
  }

  const entryNum = parseFloat(simEntry);
  const liqWarn =
    simResult && !isNaN(entryNum) && entryNum > 0
      ? simResult.liquidation_price > entryNum * 0.95
      : false;

  return (
    <div
      className="copilot-panel"
      role="dialog"
      aria-label="KORU 코파일럿"
    >
      {/* Header */}
      <div className="copilot-header">
        <div className="copilot-header-top">
          <div className="copilot-title">
            <MessageCircle size={18} className="copilot-title-icon" />
            <span>KORU 코파일럿</span>
          </div>
          <button
            onClick={() => setOpen(false)}
            className="copilot-close"
            aria-label="닫기"
          >
            <X size={16} />
          </button>
        </div>
        <div className="copilot-tabs" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === "chat"}
            onClick={() => setActiveTab("chat")}
            className="copilot-tab"
          >
            <MessagesSquare size={14} />
            채팅
          </button>
          <button
            role="tab"
            aria-selected={activeTab === "sim"}
            onClick={() => setActiveTab("sim")}
            className="copilot-tab"
          >
            <Calculator size={14} />
            시뮬레이터
          </button>
        </div>
      </div>

      {activeTab === "chat" ? (
        <>
          {/* Messages */}
          <div className="copilot-body">
            {messages.map((msg, idx) => (
              <div
                key={idx}
                className={`copilot-msg ${msg.role}`}
              >
                <div className="copilot-bubble">
                  {msg.role === "assistant" ? (
                    <div
                      className="copilot-md"
                      dangerouslySetInnerHTML={{ __html: renderMarkdown(msg.content) }}
                    />
                  ) : (
                    <p className="copilot-msg-user-text">{msg.content}</p>
                  )}
                  {msg.toolsUsed && msg.toolsUsed.length > 0 && (
                    <div className="copilot-tools">
                      {msg.toolsUsed.map((tool) => (
                        <span key={tool} className="copilot-tool-chip">
                          {tool}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {loading && (
              <div className="copilot-loading">
                <span className="copilot-typing-dots" aria-hidden="true">
                  <span />
                  <span />
                  <span />
                </span>
                심층 퀀트 분석 및 데이터 RAG 추론 중... ({loadingSecs}초)
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {!loading && pendingJob.current && <div className="copilot-sim-hint">
            <p>기존 요청 ID를 유지해 결과를 재조회합니다. 새 분석은 별도 유료 요청입니다.</p>
            <button className="btn-secondary" onClick={() => {
              pendingJob.current = null;
              savePendingJob("copilot", null);
              setMessages(prev => [...prev, { role: "assistant", content: "새 요청으로 분석할 수 있습니다. 질문을 입력하고 전송해 주세요." }]);
            }}>새 분석 준비</button>
          </div>}
          {/* Input */}
          <div className="copilot-input-bar">
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="질문을 입력하세요..."
              disabled={loading}
              className="copilot-input"
              aria-label="질문 입력"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              className="copilot-send"
              aria-label="전송"
            >
              <Send size={16} />
            </button>
          </div>
        </>
      ) : (
        /* Simulator tab */
        <div className="copilot-sim">
          <div className="copilot-sim-hint">
            가상 포지션의 청산가, 펀딩 비용, 수수료를 계산합니다.
          </div>
          <label className="copilot-field">
            <span className="copilot-field-label">진입 가격 (USDT)</span>
            <input
              type="number"
              value={simEntry}
              onChange={(e) => setSimEntry(e.target.value)}
              className="copilot-field-input"
            />
          </label>
          <div className="grid grid-2" style={{ gap: "var(--space-2)" }}>
            <label className="copilot-field">
              <span className="copilot-field-label">수량</span>
              <input
                type="number"
                value={simQty}
                onChange={(e) => setSimQty(e.target.value)}
                className="copilot-field-input"
              />
            </label>
            <label className="copilot-field">
              <span className="copilot-field-label">레버리지 (x)</span>
              <input
                type="number"
                value={simLev}
                onChange={(e) => setSimLev(e.target.value)}
                className="copilot-field-input"
              />
            </label>
          </div>
          <div className="grid grid-2" style={{ gap: "var(--space-2)" }}>
            <label className="copilot-field">
              <span className="copilot-field-label">시간당 펀딩비율</span>
              <input
                type="number"
                step="0.0001"
                value={simFunding}
                onChange={(e) => setSimFunding(e.target.value)}
                className="copilot-field-input"
              />
            </label>
            <label className="copilot-field">
              <span className="copilot-field-label">유지 시간 (h)</span>
              <input
                type="number"
                value={simHours}
                onChange={(e) => setSimHours(e.target.value)}
                className="copilot-field-input"
              />
            </label>
          </div>
          <button
            onClick={handleSimulate}
            disabled={simLoading}
            className="copilot-sim-run"
          >
            {simLoading ? "계산 중..." : "시뮬레이션 실행"}
          </button>

          {simResult && (
            <div className="copilot-sim-result">
              <div className="copilot-sim-result-title">결과</div>
              <div className="copilot-sim-rows">
                <SimRow label="명목 가치" value={`$${simResult.notional.toFixed(2)}`} />
                <SimRow label="증거금" value={`$${simResult.margin.toFixed(2)}`} />
                <SimRow
                  label="청산가"
                  value={`$${simResult.liquidation_price.toFixed(2)}`}
                  valueClass={liqWarn ? "warn" : undefined}
                />
                <div className="copilot-sim-divider" />
                <SimRow label="펀딩 비용" value={`$${simResult.funding_cost.toFixed(4)}`} />
                <SimRow label="수수료" value={`$${simResult.fees.toFixed(4)}`} />
                <SimRow
                  label="총 마찰비용"
                  value={`$${simResult.total_friction.toFixed(4)}`}
                  valueClass="danger"
                />
                <SimRow
                  label="ROE 손익분기"
                  value={`${simResult.roe_breakeven_pct.toFixed(2)}%`}
                />
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SimRow({
  label,
  value,
  valueClass,
}: {
  label: string;
  value: string;
  valueClass?: string;
}) {
  return (
    <div className="copilot-sim-row">
      <span className="copilot-sim-row-label">{label}</span>
      <span className={`copilot-sim-row-value ${valueClass ?? ""}`}>{value}</span>
    </div>
  );
}
